import React, { useMemo, useState } from "react";
import "bootstrap/dist/css/bootstrap.min.css";
import { Button, Card, Col, Form, Row, Spinner, Badge } from "react-bootstrap";
import { toast } from "react-toastify";
import { redeemSevenKnightCoupon } from "../../services/sevenKnightService";

const parseLines = (text) =>
  text
    .split("\n")
    .map((x) => x.replace(/\u200B/g, "").trim()) // ✅ ลบ zero-width เผื่อคูปองแฝง
    .filter(Boolean);

const ERROR_CODE_TH = {
    200: {
        title: "สำเร็จ",
        detail: "แลกคูปองสำเร็จ",
    },
    21002: {
        title: "UID ไม่ถูกต้อง",
        detail: "กรุณาตรวจสอบ UID/เลขสมาชิก แล้วลองใหม่อีกครั้ง",
    },
    24001: {
        title: "ระงับการใช้ชั่วคราว",
        detail: "คุณป้อนหมายเลขคูปองไม่ถูกต้องติดต่อกัน 10 ครั้ง คุณสามารถลองใหม่อีกครั้งในอีก 1 ชั่วโมง",
    },
    24002: {
        title: "คูปองไม่ถูกต้อง",
        detail: "คูปองไม่ถูกต้องหรือพิมพ์ผิด กรุณาตรวจสอบแล้วลองใหม่",
    },
    24004: {
        title: "ใช้คูปองเกินจำนวน",
        detail: "คูปองนี้ถูกใช้ครบจำนวนครั้งที่กำหนดแล้ว",
    },
};

const getCodeText = (code) => {
  if (code == null) return null;
  return ERROR_CODE_TH[Number(code)] || {
    title: `เกิดข้อผิดพลาด (${code})`,
    detail: "ไม่สามารถแลกคูปองได้ กรุณาลองใหม่อีกครั้ง",
  };
};

const buildRewardText = (item) => {
  const rewards = item?.step1?.resultData || [];
  const names = rewards.map((x) => x?.productName).filter(Boolean);

  const qty = item?.step2?.resultData?.[0]?.quantity;
  if (!names.length) return "แลกสำเร็จ (ไม่มีรายละเอียดรางวัล)";
  if (qty != null) return `ได้รับ: ${names.join(", ")} (x${qty})`;
  return `ได้รับ: ${names.join(", ")}`;
};

const deriveStatus = (item) => {
  const step1Code = item?.step1?.errorCode;
  const step2Ok = item?.step2Ok === true || item?.step2?.errorCode === 200;

  // ล้มเหลวตั้งแต่ step1
  if (step1Code !== 200) {
    const th = getCodeText(step1Code);
    const detail = th?.detail || "แลกไม่สำเร็จ";
    return {
      ok: false,
      badgeClass: "bg-danger",
      label: th?.title || "FAILED",
      message: `${detail}`,
    };
  }

  // step1 ผ่าน แต่ step2 ไม่ผ่าน => ก้ำกึ่ง
  if (!step2Ok) {
    return {
      ok: false,
      badgeClass: "bg-warning text-dark",
      label: "ดำเนินการไม่ครบ",
      message: "ยืนยันคูปองผ่านแล้ว แต่ขั้นตอนแจกไอเทมไม่สำเร็จ (กรุณาลองใหม่/ตรวจสอบภายหลัง)",
    };
  }

  // สำเร็จจริง
  return {
    ok: true,
    badgeClass: "bg-success",
    label: "สำเร็จ",
    message: buildRewardText(item),
  };
};

const Redeem = () => {
  const [couponsText, setCouponsText] = useState("");
  const [uidsText, setUidsText] = useState("");

  const [loading, setLoading] = useState(false);
  const [progress, setProgress] = useState({ done: 0, total: 0 });

  const [results, setResults] = useState([]);
  const [uidFilter, setUidFilter] = useState(""); // "" = ทั้งหมด

  const uidOptions = useMemo(() => {
    const set = new Set(results.map((r) => String(r.uid)));
    return Array.from(set).sort();
  }, [results]);

  const filteredResults = useMemo(() => {
    if (!uidFilter) return results;
    return results.filter((r) => String(r.uid) === String(uidFilter));
  }, [results, uidFilter]);

  const handleClear = () => {
    setCouponsText("");
    setUidsText("");
    setResults([]);
    setUidFilter("");
    setProgress({ done: 0, total: 0 });
  };

  const normalizeToRows = (data) => {
    const list = Array.isArray(data?.results)
      ? data.results
      : Array.isArray(data)
      ? data
      : null;

    if (!list) {
      return [
        {
          id: `${Date.now()}-${Math.random()}`,
          uid: "(bulk)",
          coupon: "(bulk)",
          ok: false,
          badgeClass: "bg-danger",
          statusLabel: "รูปแบบข้อมูลไม่ถูกต้อง",
          displayMessage: "API ส่งข้อมูลกลับมาไม่ใช่รูปแบบ results[]",
          raw: data,
          time: new Date(),
        },
      ];
    }

    return list.map((item) => {
      const uid = item?.uid ?? "";
      const coupon = item?.coupon_code ?? "";

      const status = deriveStatus(item);

      return {
        id: `${Date.now()}-${Math.random()}`,
        uid,
        coupon,
        ok: status.ok, // ✅ อิงตามผล redeem จริง
        badgeClass: status.badgeClass,
        statusLabel: status.label,
        displayMessage: status.message, // ✅ ข้อความไทยสรุป
        raw: item, // ✅ เก็บ raw ไว้ดูรายละเอียด
        time: new Date(),
      };
    });
  };

  const handleRedeem = async () => {
    const coupons = parseLines(couponsText);
    const uids = parseLines(uidsText);

    if (coupons.length === 0) return toast.error("กรุณาใส่คูปองอย่างน้อย 1 บรรทัด");
    if (uids.length === 0) return toast.error("กรุณาใส่ UID อย่างน้อย 1 บรรทัด");

    const total = coupons.length * uids.length;
    setProgress({ done: 0, total });
    setLoading(true);

    try {
      const payload = {
        uid: uids,
        coupon_code: coupons,
      };

      const data = await redeemSevenKnightCoupon(payload);

      const rows = normalizeToRows(data);

      // ของใหม่ขึ้นก่อน
      setResults((prev) => [...rows.reverse(), ...prev]);

      setProgress({ done: total, total });
      toast.success(`Redeem เสร็จแล้ว (${total} รายการ)`);
    } catch (err) {
      const response = err?.response?.data || { message: err?.message || "Error" };

      setResults((prev) => [
        {
          id: `${Date.now()}-${Math.random()}`,
          uid: "(bulk)",
          coupon: "(bulk)",
          ok: false,
          badgeClass: "bg-danger",
          statusLabel: "FAILED",
          displayMessage: response?.message || "Redeem ไม่สำเร็จ",
          raw: response,
          time: new Date(),
        },
        ...prev,
      ]);

      toast.error("Redeem ไม่สำเร็จ");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="card shadow-sm border-0">
      <div className="card-body p-4">
        {/* Header */}
        <div className="d-flex flex-wrap justify-content-between align-items-center gap-2 mb-3">
          <div>
            <h3 className="m-0">Seven Knight - Redeem Coupon</h3>
            <div className="text-muted small mt-1">
              Redeem และแสดงสถานะตามผลลัพธ์
            </div>
          </div>

          <div className="d-flex gap-2">
            <Button variant="outline-secondary" onClick={handleClear} disabled={loading}>
              ล้างค่า
            </Button>
            <Button variant="primary" onClick={handleRedeem} disabled={loading}>
              {loading ? (
                <>
                  <Spinner size="sm" className="me-2" />
                  กำลังส่ง {progress.done}/{progress.total}
                </>
              ) : (
                "Redeem"
              )}
            </Button>
          </div>
        </div>

        {/* Inputs */}
        <Row className="g-3">
          <Col xs={12} md={6}>
            <Card className="border-0 bg-light h-100">
              <Card.Body>
                <div className="fw-semibold mb-2">คูปอง (1 บรรทัดต่อ 1 คูปอง)</div>
                <Form.Control
                  as="textarea"
                  rows={10}
                  className="font-monospace"
                  value={couponsText}
                  onChange={(e) => setCouponsText(e.target.value)}
                  disabled={loading}
                />
              </Card.Body>
            </Card>
          </Col>

          <Col xs={12} md={6}>
            <Card className="border-0 bg-light h-100">
              <Card.Body>
                <div className="fw-semibold mb-2">UID (1 บรรทัดต่อ 1 UID)</div>
                <Form.Control
                  as="textarea"
                  rows={10}
                  className="font-monospace"
                  value={uidsText}
                  onChange={(e) => setUidsText(e.target.value)}
                  disabled={loading}
                />
              </Card.Body>
            </Card>
          </Col>
        </Row>

        {/* Filter + Response */}
        <div className="mt-4 d-flex flex-wrap justify-content-between align-items-end gap-2">
          <div>
            <div className="fw-semibold">ผลลัพธ์</div>
            <div className="text-muted small">สถานะอิงจาก 7k</div>
          </div>

          <div style={{ minWidth: 240 }}>
            <Form.Label className="small text-muted mb-1">Filter ตาม UID</Form.Label>
            <Form.Select
              value={uidFilter}
              onChange={(e) => setUidFilter(e.target.value)}
              disabled={!results.length}
            >
              <option value="">ทั้งหมด</option>
              {uidOptions.map((uid) => (
                <option key={uid} value={uid}>
                  {uid}
                </option>
              ))}
            </Form.Select>
          </div>
        </div>

        <div className="mt-2">
          {!results.length ? (
            <div className="text-muted text-center py-4">
              ยังไม่มีผลลัพธ์ — ใส่คูปองและ UID แล้วกด Redeem
            </div>
          ) : (
            <div className="border rounded-3 overflow-auto" style={{ maxHeight: 420 }}>
              <table className="table table-sm table-hover table-striped mb-0 align-middle">
                <thead className="table-light" style={{ position: "sticky", top: 0, zIndex: 1 }}>
                  <tr className="text-center">
                    <th style={{ width: "5%" }}>#</th>
                    <th style={{ width: "18%" }}>UID</th>
                    <th style={{ width: "18%" }}>Coupon</th>
                    <th style={{ width: "14%" }}>Status</th>
                    <th>Response</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredResults.map((r, idx) => (
                    <tr key={r.id}>
                      <td className="text-center">{idx + 1}</td>
                      <td className="text-center">{r.uid}</td>
                      <td className="text-center">{r.coupon}</td>
                      <td className="text-center">
                        <Badge className={r.badgeClass}>{r.statusLabel}</Badge>
                      </td>
                      <td>
                        <div className="small">{r.displayMessage}</div>

                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {results.length > 0 && (
          <div className="text-muted small mt-2">
            แสดงอยู่ {filteredResults.length} รายการ {uidFilter ? `(เฉพาะ UID: ${uidFilter})` : ""}
          </div>
        )}
      </div>
    </div>
  );
};

export default Redeem;
